import { ExperimentsType } from "@hopper-b2b/types";

type LocalExperimentOverrides = Record<string, string>;

type ImportMetaWithGlob = ImportMeta & {
  glob?: (
    pattern: string,
    options?: {
      eager?: boolean;
    }
  ) => Record<string, unknown>;
};

type WebpackRequireContext = ((id: string) => unknown) & {
  keys: () => string[];
};

type WebpackRequire = NodeRequire & {
  context?: (
    path: string,
    useSubdirectories?: boolean,
    regExp?: RegExp
  ) => WebpackRequireContext;
};

let cachedOverrides: LocalExperimentOverrides | null = null;
let hasAttemptedLoad = false;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getModuleValue = (moduleValue: unknown): unknown => {
  if (!isObject(moduleValue)) return moduleValue;
  if ("default" in moduleValue) return moduleValue.default;
  return moduleValue;
};

const normalizeOverrides = (value: unknown): LocalExperimentOverrides => {
  if (!isObject(value)) return {};

  const normalized: LocalExperimentOverrides = {};

  for (const [experimentName, variant] of Object.entries(value)) {
    if (typeof variant === "string" && variant.length > 0) {
      normalized[experimentName] = variant.toLowerCase();
    }
  }

  return normalized;
};

const loadFromVite = (): LocalExperimentOverrides => {
  const modules = (import.meta as ImportMetaWithGlob).glob?.(
    "../../../local-experiments.config.ts",
    {
      eager: true,
    }
  );

  if (!modules) return {};

  const moduleValue = modules["../../../local-experiments.config.ts"];
  return normalizeOverrides(getModuleValue(moduleValue));
};

const loadFromWebpack = (): LocalExperimentOverrides => {
  const webpackRequire = require as WebpackRequire;
  const context = webpackRequire.context?.(
    "../../../",
    false,
    /^\.\/local-experiments\.config\.ts$/
  );

  if (!context) return {};

  const [modulePath] = context.keys();

  if (!modulePath) return {};

  return normalizeOverrides(getModuleValue(context(modulePath)));
};

const loadOverrides = (): LocalExperimentOverrides => {
  if (hasAttemptedLoad) return cachedOverrides ?? {};

  hasAttemptedLoad = true;

  if (process.env.NODE_ENV !== "development") {
    cachedOverrides = {};
    return cachedOverrides;
  }

  try {
    const viteOverrides = loadFromVite();
    if (Object.keys(viteOverrides).length > 0) {
      cachedOverrides = viteOverrides;
      return cachedOverrides;
    }

    const webpackOverrides = loadFromWebpack();
    cachedOverrides = webpackOverrides;
    return cachedOverrides;
  } catch {
    cachedOverrides = {};
    return cachedOverrides;
  }
};

export const getLocalExperimentOverrides = (): ExperimentsType =>
  loadOverrides();

export const getLocalExperimentVariant = (
  experimentName: string
): string | undefined => getLocalExperimentOverrides()[experimentName];
